'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function HeroSection() {
  const barRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (barRef?.current) {
      barRef.current.style.animation = 'none';
      void barRef?.current?.offsetHeight;
      barRef.current.style.animation = '';
    }
  }, []);

  return (
    <header
      className="relative overflow-hidden"
      style={{
        background:
          'radial-gradient(1100px 620px at 82% -10%, rgba(201,137,65,0.16), transparent 60%), var(--navy-deep)',
        paddingTop: '104px',
        paddingBottom: '110px',
      }}
    >
      <div className="max-w-site mx-auto px-7">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
          {/* Left */}
          <div>
            <div className="eyebrow">Dedicated cold calling reps</div>
            <h1 className="heading-1 text-white">
              Reps who pick up the phone.
            </h1>
            <p className="mt-5 text-[20px] text-muted-cream leading-relaxed max-w-[62ch]">
              CommandVA gives you full-time, dedicated sales development reps who put cold calling first, supported by email and LinkedIn, inside a complete targeting and reporting system. Start with one rep or build a team.
            </p>
            <div className="flex flex-wrap gap-3.5 mt-9">
              <a
                href="/request-meeting"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold"
              >
                Book a strategy call
              </a>
              <Link
                href="/math"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] border border-line-navy text-white transition-all duration-200 hover:border-primary hover:text-primary"
              >
                See the math
              </Link>
            </div>

            {/* Proof points */}
            <div className="flex flex-wrap gap-8 mt-11">
              {[
                { val: '65%+', label: 'Below in-house cost' },
                { val: '10 days', label: 'Avg. time to deploy' },
                { val: '$0', label: 'Setup fees or lock-in' },
              ]?.map((p) => (
                <div key={p?.label} className="font-mono">
                  <b className="block text-[22px] text-white font-semibold">{p?.val}</b>
                  <span className="text-[11.5px] tracking-[0.12em] uppercase text-muted-cream">
                    {p?.label}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-6 font-mono text-[13px] tracking-[0.14em] text-primary uppercase">
              We book the meetings. You close the deals.
            </p>
          </div>

          {/* Console widget */}
          <div
            role="img"
            aria-label="Sample weekly client report"
            className="relative rounded-xl2 font-mono shadow-console border border-line-navy overflow-hidden"
            style={{
              background: 'linear-gradient(160deg, var(--navy-panel), var(--navy-deep))',
              padding: '26px 26px 22px',
            }}
          >
            {/* Header */}
            <div className="flex justify-between items-center text-[11.5px] tracking-[0.16em] uppercase text-muted-cream border-b border-line-navy pb-3.5 mb-4">
              <span>SAMPLE CLIENT REPORT</span>
              <b className="text-primary font-semibold">WEEK 28</b>
            </div>

            {/* Rows */}
            <div className="flex justify-between text-[14.5px] text-muted-cream py-1.5">
              <span>Dials</span>
              <b className="text-white font-semibold">412</b>
            </div>

            {/* Progress bar */}
            <div className="h-[7px] bg-navy-deep rounded-full overflow-hidden my-1.5">
              <i
                ref={barRef}
                className="block h-full rounded-full animate-bar-fill"
                style={{
                  width: '82%',
                  background: 'linear-gradient(90deg, var(--gold), var(--gold-bright))',
                }}
              />
            </div>

            <div className="flex justify-between text-[14.5px] text-muted-cream py-1.5">
              <span>Live conversations</span>
              <b className="text-[#8FD0A0] font-semibold">57</b>
            </div>
            <div className="flex justify-between text-[14.5px] text-muted-cream py-1.5">
              <span>Meetings booked</span>
              <b className="text-white font-semibold">8</b>
            </div>
            <div className="flex justify-between text-[14.5px] text-muted-cream py-1.5">
              <span>Show rate</span>
              <b className="text-white font-semibold">84%</b>
            </div>
            <div className="flex justify-between text-[14.5px] text-muted-cream py-1.5">
              <span>Email + LinkedIn replies</span>
              <b className="text-white font-semibold">24</b>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2.5 mt-3.5 pt-3.5 border-t border-line-navy text-[12px] tracking-[0.14em] uppercase text-[#8FD0A0]">
              <span className="w-2 h-2 rounded-full bg-[#8FD0A0] flex-shrink-0 animate-dot-pulse" />
              ON TARGET · NEXT REVIEW FRI 9:00 CT
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}